"use client";

import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslations } from "next-intl";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import logger from "@/lib/logger";

function ErrorFallbackUI({ error, errorInfo, onRetry }: { error: Error; errorInfo?: React.ErrorInfo; onRetry: () => void }) {
	const t = useTranslations("errorBoundary");
	return (
		<div
			className="min-h-screen flex items-center justify-center p-4"
			role="alert"
			aria-live="assertive"
		>
			<Card className="max-w-2xl w-full">
				<CardHeader>
					<CardTitle className="flex items-center gap-3 text-destructive">
						<AlertTriangle className="w-6 h-6" aria-hidden="true" />
						{t("title")}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<Alert variant="destructive">
						<AlertTriangle className="h-4 w-4" />
						<AlertDescription>
							{t("description")}
							<ul className="list-disc list-inside mt-2 space-y-1">
								<li>{t("reason1")}</li>
								<li>{t("reason2")}</li>
								<li>{t("reason3")}</li>
								<li>{t("reason4")}</li>
							</ul>
						</AlertDescription>
					</Alert>

					{process.env.NODE_ENV === "development" && error && (
						<details className="mt-4">
							<summary className="cursor-pointer font-semibold mb-2">
								{t("technicalDetails")}
							</summary>
							<div className="bg-muted p-4 rounded-md text-sm font-mono overflow-auto">
								<div className="mb-2">
									<strong>{t("errorLabel")}</strong> {error.message}
								</div>
								{error.stack && (
									<div className="mb-2">
										<strong>{t("stackTrace")}</strong>
										<pre className="whitespace-pre-wrap text-xs mt-1">
											{error.stack}
										</pre>
									</div>
								)}
								{errorInfo?.componentStack && (
									<div>
										<strong>{t("componentStack")}</strong>
										<pre className="whitespace-pre-wrap text-xs mt-1">
											{errorInfo.componentStack}
										</pre>
									</div>
								)}
							</div>
						</details>
					)}

					<div className="flex flex-col sm:flex-row gap-3">
						<Button
							onClick={onRetry}
							className="flex items-center gap-2"
							aria-label={t("tryAgain")}
						>
							<RefreshCw className="w-4 h-4" aria-hidden="true" />
							{t("tryAgain")}
						</Button>
						<Button
							variant="outline"
							onClick={() => window.location.reload()}
							aria-label={t("reloadPage")}
						>
							{t("reloadPage")}
						</Button>
						<Button
							variant="ghost"
							onClick={() => (window.location.href = "/")}
							aria-label={t("goHome")}
						>
							{t("goHome")}
						</Button>
					</div>

					<div className="text-sm text-muted-foreground">
						<p>{t("helpText")}</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
	errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
	children: React.ReactNode;
	fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
}

export class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {
			hasError: true,
			error,
		};
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		logger.error({ err: error, errorInfo }, "ErrorBoundary caught an error");
		this.setState({
			error,
			errorInfo,
		});

		// Report to analytics or error tracking service
		if (typeof window !== "undefined") {
			// You can integrate with services like Sentry here
			logger.error({
				error: error.message,
				stack: error.stack,
				componentStack: errorInfo.componentStack,
			}, "Error caught by boundary");
		}
	}

	handleRetry = () => {
		this.setState({ hasError: false, error: undefined, errorInfo: undefined });
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				const FallbackComponent = this.props.fallback;
				return (
					<FallbackComponent
						error={this.state.error!}
						retry={this.handleRetry}
					/>
				);
			}

			return (
				<ErrorFallbackUI
					error={this.state.error!}
					errorInfo={this.state.errorInfo}
					onRetry={this.handleRetry}
				/>
			);
		}

		return this.props.children;
	}
}